<script>
export default {
  title: 'Assignment 1.1: UNIX Basics',
  starterId: null
}
</script>

<template>
  <section>

    <Readings :readings="[
      {
        title: '<cite>Your UNIX/LINUX: The Ultimate Guide (3rd Edition)</cite> Chapter 1'
      },
      {
        title: '<cite>Your UNIX/LINUX: The Ultimate Guide (3rd Edition)</cite> Chapter 2 (excluding Sections 2.9 and 2.15)'
      },
      {
        title: '<cite>Your UNIX/LINUX: The Ultimate Guide (3rd Edition)</cite> Chapter 3'
      },
      {
        title: '<cite>Your UNIX/LINUX: The Ultimate Guide (3rd Edition)</cite> Chapter 4'
      }
    ]"/>

    <article>
      <h2>Overview</h2>

      <p>This assignment will give you an introduction to UNIX; you will learn
        about common UNIX commands and how to navigate the file system.  The
        learning curve for UNIX is quite steep for those unaccustomed to command
        line interaction with computers, so try not to be discouraged if this first
        assignment seems daunting.  There are a lot of common UNIX commands, so if
        this deluge of information seems difficult to handle, try making a
        cheat-sheet on an index card and keep it with you as you work through the
        course.</p>

      <p>Additionally, please do not attempt to do the entire reading before
        exploring the system.  Most people learn by doing rather than reading, so
        as you are progressing through the reading for this assignment, take some
        time to explore the commands being discussed as you learn about them.</p>

      <p>As always, the tutors at the SPC are there to help you, so please come
        in and take advantage of their knowledge if you are having trouble getting
        started.</p>
    </article>

    <article>
      <h2>Commands</h2>
      <p>The following commands may be particularly useful to you: head, tail,
        echo, cat, ls, find.  This is not a complete list; you will need to use
        other commands to complete the assignment.</p>
    </article>

    <article>
      <h2>Part 1: System Information</h2>

      <p>You should turn in a list of the commands used to answer the questions,
        and be prepared to execute them to demonstrate their use to a tutor.</p>

      <p><b>What would you execute to determine...</b></p>

      <ol>
        <li>
          <p>the hostname of the system you are on?</p>
        </li>
        <li>
          <p>
            the type and version of the operating system the machine machine is running?
            (Show both at the same time, with one command.)
          </p>
        </li>
        <li>
          <p>the full path to your home directory?</p>
        </li>
        <li>
          <p>which other users are logged into the machine you are using?</p>
        </li>
        <li>
          <p>
            how many words there are in the spell check dictionary (/usr/share/lib/dict/words)?
            (If your computer doesn't have that file, test this answer on an instructional machine.)
          </p>
        </li>
        <li>
          <p>what groups you belong to?</p>
        </li>
        <li>
          <p>
            what files (excluding directories) are located in your home directory
            and all its subdirectories?
          </p>
        </li>
        <li>
          <p>what man pages have references to 'bash'?</p>
        </li>
      </ol>
    </article>

    <article>
      <h2>Part 2: Your Website</h2>

      <p>
        Not many students realize this, but every instructional account has a
        website associated with it. The address of this website is
        <code>http://inst.eecs.berkeley.edu/~&lt;username&gt;</code> where <code>&lt;username&gt;</code> is your login
        (cs9e-1ab for example). The files that are hosted here are located at
        <code>~/public_html</code>. You will need this folder for the website to work.
        Users viewing these pages view them through the Apache
        web server which is run as a special user.  File permissions for your
        web site must be configured such that this special user can access your
        files.  Under most circumstances, this means that "world" or "other"
        permissions are used to designate what web browsers have access to.
      </p>

      <p>
        Again, write down your answers and bring them in for a tutor to evaluate, and
        be prepared to explain why those chmod statements had the effects.
      </p>

      <p><b>Your tasks:</b></p>
      <ol>
        <li>
          <p>Create a folder named "public_html" in your home directory.
            Place a 'readme.txt' file in there.  (It should have the path: ~/public_html/readme.txt.
            Set up file permissions so that
            you can see the directory listing and download 'readme.txt' from the browser.
            What commands did you use for this?</p>
        </li>

        <li>
          <p>Suppose that your working directory is your top-level directory and you give the command:</p>

          <pre v-highlightjs><code class="bash">$ chmod 711 ~/public_html</code></pre>

          <p>Can you still view the directory listing?  Can you still download readme.txt?</p>
        </li>

        <li>
          <p>Again suppose that your working directory is your top-level directory (~/public_html has its pre-2b permissions), and you give the command:</p>

          <pre v-highlightjs><code class="bash">$ chmod 600 ~/public_html/readme.txt</code></pre>

          <p>Can you still view the directory listing?  Can you still download readme.txt?</p>

        </li>

        <li>
          <p>Again suppose that your working directory is your top-level directory (~/public_html and ~/public_html/readme.txt have their pre-2b permissions), and you give the command:</p>

          <pre v-highlightjs><code class="bash">$ chmod 700 ~/</code></pre>

          <p>Can you still view the directory listing?  Can you still download readme.txt?</p>
        </li>

      </ol>

    </article>

    <article>
      <h2>Checklist</h2>

      <article>
        <h3>Part 1</h3>
        <ul>
          <li>Student correctly answers all questions and provides a transcript showing their session.</li>
        </ul>
      </article>

      <article>
        <h3>Part 2</h3>
        <ul>
          <li>Student correctly answers all 3 chmod questions and can explain why the permissions had those effects.</li>
        </ul>
      </article>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/unix/UnixBasics.vue